import React, { Fragment } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import SEO from "../../components/ceo/CEO";

const About = () => {
  return (
    <Fragment>
      <SEO
        title="About Us"
        description="Learn about SHALL Social and Development Organization, our mission to empower communities through sustainable solutions, education, and social justice."
        url="https://shallafg.org/about"
        image="https://shallafg.org/media/images/logo.png"
      />
      <Box
        sx={{
          background: "linear-gradient(180deg, #0A1128 0%, #1A2E55 100%)",
          color: "#FFFFFF",
          paddingY: "80px",
        }}
      >
        <Container maxWidth="lg">
          {/* Page Header */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              textAlign: "center",
              color: "#FFFFFF",
              marginBottom: "60px",
              textTransform: "uppercase",
              letterSpacing: "3px",
              fontSize: { xs: "2.2rem", md: "3.5rem" },
            }}
          >
            About Us
          </Typography>

          {/* Introduction */}
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "#D1D5DB",
              fontSize: { xs: "1rem", md: "1.2rem" },
              maxWidth: "800px",
              margin: "0 auto 60px",
              lineHeight: 1.7,
            }}
          >
            Welcome to SHALL Social and Development Organization. We are
            committed to empowering communities through sustainable solutions,
            education, and innovation. Our goal is to create lasting impacts
            that foster social equity and growth.
          </Typography>

          {/* Section: Goals, Vision, and History */}
          <Grid container spacing={6}>
            {/* Goals and Objectives */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#1A2E55",
                  borderRadius: "16px",
                  padding: "32px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0 16px 48px rgba(0, 123, 255, 0.4)",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: "#A9C9EA",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                    }}
                  >
                    Goals and Objectives
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#D1D5DB",
                      lineHeight: 1.8,
                      fontSize: "0.95rem",
                    }}
                  >
                    The SHALL Social & Development Organization aims to provide
                    quality education for underprivileged children, empower
                    youth with leadership skills, advocate for social justice,
                    foster youth political participation, promote sustainable
                    development, and nurture moral responsibility in young
                    leaders.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Our Vision */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#1A2E55",
                  borderRadius: "16px",
                  padding: "32px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0 16px 48px rgba(0, 123, 255, 0.4)",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: "#A9C9EA",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                    }}
                  >
                    Our Vision
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#D1D5DB",
                      lineHeight: 1.8,
                      fontSize: "0.95rem",
                    }}
                  >
                    The SHALL Social & Development Organization envisions a
                    world where every child, regardless of background, has
                    access to quality education and opportunities for growth. We
                    aim to empower marginalized communities and advocate for
                    social justice by fostering future leaders equipped for
                    sustainable change.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* History */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#1A2E55",
                  borderRadius: "16px",
                  padding: "32px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0 16px 48px rgba(0, 123, 255, 0.4)",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: "#A9C9EA",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                    }}
                  >
                    Our History
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#D1D5DB",
                      lineHeight: 1.8,
                      fontSize: "0.95rem",
                    }}
                  >
                    Founded by a group of educators and community leaders, the
                    SHALL Social & Development Organization has been committed
                    to addressing educational disparities and social issues
                    since 2019. Our work centers on academic support, youth
                    empowerment, and the promotion of social justice, creating
                    meaningful and lasting change in society.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Divider for separation */}
          <Divider
            sx={{
              marginY: "60px",
              backgroundColor: "#A9C9EA",
              opacity: 0.3,
            }}
          />

          {/* Bottom Section: Mission */}
          <Grid container spacing={6}>
            {/* Mission */}
            <Grid item xs={12} md={12}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  marginBottom: "24px",
                  color: "#A9C9EA",
                  textTransform: "uppercase",
                  textAlign: "center",
                }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#D1D5DB",
                  lineHeight: 1.8,
                  fontSize: "1rem",
                  textAlign: "center",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Established in 2019, the SHALL Social & Development Organization
                is a nonprofit dedicated to providing educational opportunities,
                leadership skills, and advocating for social equality. We focus
                on empowering underprivileged children and youth to unlock their
                full potential, fostering political engagement, and promoting
                sustainable development.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Fragment>
  );
};

export default About;
